import { useEffect, type PropsWithChildren } from 'react';
import { Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import Loading from '@/components/content/loading/Loading';
import { useAuth } from '@/hooks/useAuth';
import { useSession } from '@/context/SessionContext';
import { isServiceUser, isAdmin } from '@/utils/usersRole';
import { MERCURY_URL } from '@/constants/env';
import { getCurrentRelativeUrl } from '@/utils/authRedirect';
import { identifyPosthogUser } from '@/lib/posthog/identify';
import { isLoginPending, reportPendingLogin, type LoginMethod } from '@/lib/posthog/login';
import {
  clearVisitCookie,
  readSessionId,
  readVisitCookie,
  recordNewLogin,
  writeVisitCookie,
} from '@/client/features/dashboard/sections/exposure/lastVisitCookie';

import type { UserInfo } from '../types';

const SAML_EXTERNAL_AUTH = 1;

// Unknown rather than assumed, so a /userinfo failure cannot report SSO as password.
const loginMethod = (userInfo?: UserInfo): LoginMethod => {
  if (!userInfo) return 'unknown';

  return userInfo.external_auth === SAML_EXTERNAL_AUTH ? 'sso' : 'password';
};

type AuthProps = PropsWithChildren<{
  app: 'admin' | 'user';
}>;

const Auth = ({ children, app }: AuthProps) => {
  const { tokenInfo, tokenInfoQuery, userInfo, userInfoQuery } = useAuth();
  const { isSuccess, isLoading, isError } = tokenInfoQuery;
  const { startSession } = useSession();

  useEffect(() => {
    if (isSuccess && tokenInfo) {
      startSession();
      Sentry.setUser({ email: tokenInfo.sub, id: tokenInfo.user_id });
      identifyPosthogUser({
        userId: tokenInfo.user_id,
        email: tokenInfo.sub,
        role: tokenInfo.role,
        clientId: tokenInfo.client_access_id,
        clientName: tokenInfo.client_name,
        entitlements: tokenInfo.entitlements,
        isInternal: isServiceUser(tokenInfo.role),
      });
    }
  }, [isSuccess, startSession, tokenInfo]);

  const authModeSettled = userInfoQuery.isSuccess || userInfoQuery.isError;

  // Reported here, the only point both password and SSO logins reach. The visit
  // window moves only while this tab's login is still pending, so a later
  // navigation leaves the cookie as it was.
  useEffect(() => {
    if (!isSuccess || !tokenInfo || !authModeSettled) return;
    if (isServiceUser(tokenInfo.role)) return;
    if (tokenInfo.force_2fa && !tokenInfo.enabled_2fa) return;

    if (isLoginPending()) {
      recordNewLogin({
        userId: tokenInfo.user_id ? String(tokenInfo.user_id) : '',
        sessionId: readSessionId(document.cookie) ?? tokenInfo.jti ?? '',
        now: new Date(),
        read: readVisitCookie,
        write: writeVisitCookie,
        clear: clearVisitCookie,
        force: true,
      });
    }

    reportPendingLogin(loginMethod(userInfo));
  }, [isSuccess, tokenInfo, userInfo, authModeSettled]);

  const redirectToLoginWithRedirect = () => {
    const redirect = encodeURIComponent(getCurrentRelativeUrl());
    window.location.replace(`/login?redirect=${redirect}`);
  };

  if (isLoading) {
    return <Loading text="Verifying" height="100vh" />;
  }

  if (!isSuccess) {
    redirectToLoginWithRedirect();
    return null;
  }

  if (isError) {
    redirectToLoginWithRedirect();
    return null;
  }

  if (isServiceUser(tokenInfo.role)) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(getCurrentRelativeUrl())}`}
        state={{
          modal_title: 'Sorry',
          modal_text: `
          Your account doesn't have access to Mercury.
          Please try to login with a valid account or
          contact your dedicated Customer Success Manager if you need assistance.
        `,
        }}
      />
    );
  }

  if (tokenInfo.force_2fa && !tokenInfo.enabled_2fa) {
    window.location.replace(
      `${MERCURY_URL}/activate2fa?username=${encodeURIComponent(tokenInfo.sub)}`
    );
    return null;
  }

  const userIsAdmin = isAdmin(tokenInfo.role);

  if (app === 'admin' && !userIsAdmin) {
    window.location.replace('/');
    return null;
  }

  if (app === 'user' && userIsAdmin) {
    window.location.replace('/admin');
    return null;
  }

  return children;
};

export default Auth;
